import Button, {ButtonProps} from '@material-ui/core/Button'
import CircularProgress from '@material-ui/core/CircularProgress'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import {PaymentMethod} from '@stripe/stripe-js'
import ErrorAlert from 'lib/ui/alerts/ErrorAlert'
import {api} from 'lib/url'
import SaveCreditCardForm from 'lib/stripe/SaveCreditCardForm'
import {useDefaultPaymentMethod} from 'obvio/Billing/DefaultPaymentMethodProvider'
import {useOptionalPastDueRecovery} from 'obvio/Billing/PastDueRecoveryProvider'
import {useGetDelinquentSubscription} from 'obvio/Billing/subscribe'
import {useBillingTransaction} from 'obvio/Billing/BillingTransactionProvider'
import {teamMemberClient} from 'obvio/obvio-client'
import {TeamMember} from 'auth/user'
import React, {useEffect, useState} from 'react'

type NewCardFormProps = {
  className?: string
  disableAutoHide?: boolean
}

export default function NewCardForm(props: NewCardFormProps) {
  const [showingForm, setShowingForm] = useState(false)
  const {paymentMethod} = useDefaultPaymentMethod()
  const pastDueRecovery = useOptionalPastDueRecovery()
  const {updateCardFormOpen, closeUpdateCardForm} = pastDueRecovery ?? {
    updateCardFormOpen: false,
    closeUpdateCardForm: () => {},
  }
  const delinquentSubscription = useGetDelinquentSubscription()
  const isDelinquent = Boolean(delinquentSubscription)
  const {isTransactionPending} = useBillingTransaction()

  useEffect(() => {
    if (updateCardFormOpen) {
      setShowingForm(true)
    }
  }, [updateCardFormOpen])

  const handleComplete = () => {
    closeUpdateCardForm()

    if (props.disableAutoHide) {
      return
    }

    setShowingForm(false)
  }

  const label = paymentMethod ? 'Add New Default Card' : 'Add Default Card'
  const buttonVariant: ButtonProps['variant'] = paymentMethod
    ? 'outlined'
    : 'contained'

  if (!showingForm) {
    return (
      <Button
        variant={buttonVariant}
        disabled={isTransactionPending}
        onClick={() => setShowingForm(true)}
        color="primary"
        aria-label="card button"
      >
        {label}
      </Button>
    )
  }

  return (
    <Content
      {...props}
      isDelinquent={isDelinquent}
      onComplete={handleComplete}
    />
  )
}

function Content(
  props: NewCardFormProps & {isDelinquent: boolean; onComplete: () => void},
) {
  const {paymentMethod, setPaymentMethod} = useDefaultPaymentMethod()
  const pastDueRecovery = useOptionalPastDueRecovery()
  const {handleRecoverSuccess, handleRecoverError} = pastDueRecovery ?? {
    handleRecoverSuccess: () => {},
    handleRecoverError: () => {},
  }
  const [error, setError] = useState<string | null>(null)
  const clearError = () => setError(null)
  const savePaymentMethod = useSavePaymentMethod()
  const {isDelinquent, onComplete} = props
  const {isTransactionPending} = useBillingTransaction()

  const handlePaymentMethod = async (
    paymentMethodId: string | PaymentMethod,
  ) => {
    clearError()

    try {
      if (isDelinquent && pastDueRecovery) {
        const updatedUser = await teamMemberClient.post<TeamMember>(
          api('/subscribe/recover-payment'),
          {
            payment_method_id: paymentMethodId,
          },
        )
        handleRecoverSuccess(updatedUser)

        const paymentMethod = await teamMemberClient.get<PaymentMethod>(
          api('/stripe/default_payment_method'),
        )
        setPaymentMethod(paymentMethod)
        onComplete()

        return
      }

      const paymentMethod = await savePaymentMethod(paymentMethodId)
      setPaymentMethod(paymentMethod)
      onComplete()
    } catch (e: any) {
      const message = e.message || 'Unable to update payment method.'

      if (isDelinquent && pastDueRecovery) {
        handleRecoverError(message)
      }

      setError(message)
      throw e
    }
  }

  const submitLabel = isDelinquent ? 'Update Default Card and Pay' : 'Save Card'

  return (
    <>
      <ErrorAlert onClose={clearError}>{error}</ErrorAlert>
      <SaveCreditCardForm
        className={props.className}
        disabled={isTransactionPending}
        submitLabel={submitLabel}
        onSuccess={handlePaymentMethod}
      />
      {paymentMethod && (
        <Box mt={2} maxWidth={400}>
          <Typography color="textSecondary">
            *Note: Your existing card on file will be saved. The new card you
            add will become your default payment method.
          </Typography>
        </Box>
      )}
    </>
  )
}

function useSavePaymentMethod() {
  return (paymentMethodId: string | PaymentMethod) => {
    const url = api('/stripe/default_payment_method')

    return teamMemberClient.put<PaymentMethod>(url, {
      payment_method_id: paymentMethodId,
    })
  }
}

export function Loader() {
  return (
    <CircularProgress
      size={18}
      thickness={6}
      value={100}
      variant="indeterminate"
      color="primary"
    />
  )
}
